import React from 'react';
import { rooms } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const RoomDisplay = ({ room, reverse, addToCart }) => {
  const handleShopRoom = () => {
    room.products.forEach(product => {
      addToCart(product);
    });
  };

  return (
    <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-20 xl:gap-32 mb-32 lg:mb-48 items-center`}>
      {/* Image Side */}
      <div className="w-full lg:w-3/5 relative group">
        <div className="aspect-[4/5] lg:aspect-[3/4] overflow-hidden bg-[#EFECE8] relative rounded-sm">
          <picture>
            <source 
              type="image/webp" 
              srcSet={`${room.image}&w=800&q=80 800w, ${room.image}&w=1200&q=80 1200w`} 
              sizes="(max-width: 1024px) 100vw, 60vw" 
            />
            <img 
              src={`${room.image}&w=1200&q=80`} 
              alt={`Lived-in interior display for ${room.title}`} 
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-[1.03]"
              width="1200"
              height="900"
            />
          </picture>
          <div className="absolute inset-0 bg-[#8B7355] mix-blend-color opacity-5 group-hover:opacity-0 transition-opacity duration-700 pointer-events-none" />
        </div>
      </div>

      {/* Content Side */}
      <div className="w-full lg:w-2/5 flex flex-col justify-center">
        <p className="text-xs md:text-sm tracking-[0.2em] text-[#8B7355] uppercase mb-4 font-medium">{room.subtitle}</p>
        <h2 className="text-4xl md:text-5xl font-serif text-[#2C2C2C] mb-6 leading-tight text-balance">{room.title}</h2>
        <p className="text-[#595959] leading-relaxed mb-12 text-base md:text-lg font-light">
          {room.description}
        </p>

        <div className="space-y-2">
          <h3 className="text-xs font-medium tracking-[0.15em] uppercase border-b border-[#E5E5E5] pb-4 mb-6 text-[#737373]">Featured in this room</h3>
          {room.products.map((product) => (
            <div 
              key={product.id} 
              className="flex items-center gap-6 group py-4 border-b border-transparent hover:border-[#E5E5E5] transition-colors"
            >
              <div className="w-20 h-24 bg-[#EFECE8] overflow-hidden shrink-0 rounded-sm">
                <img 
                  src={`${product.image}&w=200&q=80`} 
                  alt={product.name} 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  width="200"
                  height="240"
                />
              </div>
              <div className="flex-1">
                <h4 className="text-base md:text-lg text-[#2C2C2C] group-hover:text-[#8B7355] transition-colors font-medium">{product.name}</h4>
                <p className="text-[#737373] text-sm mt-1">${product.price}</p>
              </div>
              <button 
                onClick={() => addToCart(product)}
                aria-label={`Add ${product.name} to cart`}
                className="w-10 h-10 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#2C2C2C] hover:bg-[#2C2C2C] hover:border-[#2C2C2C] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
              >
                <SafeIcon icon={FiIcons.FiPlus} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        
        <button 
          onClick={handleShopRoom}
          className="mt-12 bg-transparent border border-[#2C2C2C] text-[#2C2C2C] py-4 px-8 tracking-[0.15em] text-xs md:text-sm uppercase hover:bg-[#2C2C2C] hover:text-white transition-colors duration-300 self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
        >
          Shop Entire Room
        </button>
      </div>
    </div>
  );
};

const LookbookSection = ({ addToCart }) => {
  return (
    <section id="lookbook" className="py-24 md:py-32 px-6 md:px-12 max-w-[1600px] mx-auto bg-[#F9F8F6]">
      <div className="text-center mb-20 md:mb-32 max-w-2xl mx-auto">
        <h2 className="text-xs md:text-sm tracking-[0.2em] uppercase text-[#737373] mb-4 font-medium">Curated Spaces</h2>
        <h3 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#2C2C2C]">Shop the Lookbook</h3>
      </div>
      
      {rooms.map((room, index) => (
        <RoomDisplay key={room.id} room={room} reverse={index % 2 !== 0} addToCart={addToCart} />
      ))}
    </section>
  );
};

export default LookbookSection;